import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconArrowRight } from '@iwoca/icons';
import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import { Input, Label, Message, Radio } from '@iwoca/orion';
import { clsx } from 'clsx';
import { FormProvider, useForm } from 'react-hook-form';

import styles from './CompanyDetailsUK.module.css';
import { reductionSchema, schema } from './CompanyDetailsUK.schema';
import CompanyInputUK from './CompanyInputUK/CompanyInputUK';
import TurnoverInputUK from './TurnoverInputUK';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useSignupRouterUK } from '../../../hooks/useSignupRouter/useSignupRouterUK';
import { useSplitTest } from '../../../hooks/useSplitTest';
import {
  useGetSelectStateQuery,
  useGetStateQuery,
} from '../../../lendingApi/lendingApiQueries';
import { TGetStateResponse } from '../../../types';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import { Button } from '../../Button/Button';
import { EditAmount } from '../../EditAmount/EditAmount';
import {
  denormaliseCompanyDataUK,
  normaliseStepData,
} from '../../helpers/stateNormalisation';
import { BUSINESS_STEP_ID } from '../../helpers/steps';
import AccountCreatedModal from '../../SignupRoutes/AccountCreatedModal/AccountCreatedModal';

export type TCompanyDetailsDataUK = {
  companyName: string;
  companyNumber: string;
  companyType: GetCompanySearchResponse['data'][number]['company_type'];
  startDateMonth: string;
  startDateYear: string;
  last12MonthsTurnover: string;
  website?: string;
  isVatRegistered?: 'true' | 'false';
};

type TProps = {
  handleStateUpdateError: (error: Error) => void;
};

const CompanyDetailsUK = ({ handleStateUpdateError }: TProps) => {
  const { state, isStateFetching } = useGetStateQuery<TGetStateResponse>();

  const { normalisedState } = useGetSelectStateQuery(
    (data) => normaliseStepData(data)[BUSINESS_STEP_ID],
  );

  const stateKey = useContextStateKey();
  const { hasBusinessDetailsReductionEnabled } = useSplitTest();

  const { navigateSignup } = useSignupRouterUK();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<TCompanyDetailsDataUK>({
    resolver: yupResolver(
      hasBusinessDetailsReductionEnabled ? reductionSchema : schema,
    ),
    defaultValues: { ...normalisedState },
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = methods;

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Business details',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  // Some partners forget to send us some company info. Easier to just wipe all company info
  // and get the customer to re-enter.
  useEffect(() => {
    if (!normalisedState) return;

    if (
      !normalisedState.companyName ||
      !normalisedState.companyNumber ||
      !normalisedState.companyType ||
      !normalisedState.startDateMonth ||
      !normalisedState.startDateYear
    ) {
      setValue('companyName', '');
      setValue('companyNumber', '');
      // @ts-expect-error form state requires type to be an enum for submission
      setValue('companyType', '');
      setValue('startDateMonth', '');
      setValue('startDateYear', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalisedState]);

  const onSubmit = async (values: TCompanyDetailsDataUK) => {
    if (!state || !stateKey) {
      return;
    }

    const denormalisedState = denormaliseCompanyDataUK(state, values);

    tracking.ampli.businessDetailsSubmitted(
      getAmplitudeDefaultEventProperties(),
    );

    try {
      setIsSubmitting(true);

      await navigateSignup({
        route: 'check-eligibility',
        updatedState: denormalisedState.data,
      });
    } catch (error: unknown) {
      if (error instanceof Error) {
        handleStateUpdateError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className={styles.container}>
        <EditAmount currentStep="company_details" />
        <h1 className={styles.heading}>Introduce your business</h1>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <div className={styles.formFieldContainer}>
              <div className={styles.formField}>
                <CompanyInputUK />
              </div>
              {!hasBusinessDetailsReductionEnabled && (
                <Input
                  {...register('website')}
                  wrapperClassName={styles.formField}
                  label="Website (optional)"
                  placeholder="www.example.com"
                  error={errors.website?.message}
                />
              )}
              <div className={styles.formField}>
                <TurnoverInputUK />
              </div>
              {!hasBusinessDetailsReductionEnabled && (
                <div className={styles.formField}>
                  <Label className={styles.formLabel}>
                    Is the business VAT registered?
                  </Label>
                  <div className={styles.radioOptions}>
                    <Radio
                      {...register('isVatRegistered')}
                      wrapperClassName={clsx(styles.radioOption, {
                        [styles.errorContainer]: errors.isVatRegistered,
                      })}
                      frame
                      value="true"
                      label="Yes"
                    />
                    <Radio
                      {...register('isVatRegistered')}
                      wrapperClassName={clsx(styles.radioOption, {
                        [styles.errorContainer]: errors.isVatRegistered,
                      })}
                      frame
                      value="false"
                      label="No"
                    />
                  </div>
                  {errors.isVatRegistered && (
                    <Message
                      className={styles.vatErrorMessage}
                      message={errors.isVatRegistered.message || ''}
                      type="error"
                    />
                  )}
                </div>
              )}
            </div>
            <Button
              className={styles.submit}
              loading={isSubmitting || isStateFetching}
              type="submit"
            >
              Next step
              <IconArrowRight />
            </Button>
          </form>
        </FormProvider>
      </div>
      {stateKey && <AccountCreatedModal stateKey={stateKey} />}
    </>
  );
};

export default CompanyDetailsUK;
